import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Upload, 
  Form, 
  Input, 
  message, 
  Space,
  Card,
  Progress,
  Select,
  Dropdown
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  DownloadOutlined,
  UploadOutlined,
  ReloadOutlined,
  MobileOutlined,
  DownOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import axios from 'axios';

const { Option } = Select;

const APKList = () => {
  const [apks, setApks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadVisible, setUploadVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [installVisible, setInstallVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedAPK, setSelectedAPK] = useState(null);
  const [form] = Form.useForm();
  const [installForm] = Form.useForm();

  useEffect(() => {
    fetchAPKs();
  }, []);

  const fetchAPKs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/apks');
      setApks(response.data.data || []);
    } catch (error) {
      message.error('获取APK列表失败');
      console.error('Fetch APKs error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (values) => {
    const { file, version, description } = values;
    
    if (!file || file.length === 0) {
      message.error('请选择APK文件');
      return;
    }

    const formData = new FormData();
    formData.append('apk', file[0].originFileObj);
    formData.append('version', version || '');
    formData.append('description', description || '');

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await api.post('/apks/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        }
      });

      if (response.data.success) {
        message.success('APK上传成功');
        setUploadVisible(false);
        form.resetFields();
        setUploadProgress(0);
        fetchAPKs();
      } else {
        message.error(response.data.message || 'APK上传失败');
      }
    } catch (error) {
      message.error('APK上传失败');
      console.error('Upload APK error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (apkId) => {
    try {
      await api.delete(`/apks/${apkId}`);
      message.success('APK删除成功');
      fetchAPKs();
    } catch (error) {
      message.error('APK删除失败');
      console.error('Delete APK error:', error);
    }
  };

  const handleDownload = async (apkId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/apks/${apkId}/download`, {
        responseType: 'blob',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `app-${apkId}.apk`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        message.error('登录已过期，请重新登录');
      } else {
        message.error('APK下载失败');
      }
      console.error('Download APK error:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const fetchDevices = async () => {
    try {
      const response = await api.get('/devices');
      setDevices(response.data.data || []);
    } catch (error) {
      message.error('获取设备列表失败');
      console.error('Fetch devices error:', error);
    }
  };

  const showInstallModal = async (apk) => {
    setSelectedAPK(apk);
    await fetchDevices();
    setInstallVisible(true);
  };

  const handleInstall = async (values) => {
    if (!selectedAPK) {
      message.error('请选择APK');
      return;
    }

    setInstalling(true);
    try {
      const response = await api.post('/commands/install', {
        deviceId: values.deviceId,
        apkId: selectedAPK.apkId
      });

      if (response.data.success) {
        message.success('安装指令已发送');
        setInstallVisible(false);
        installForm.resetFields();
      } else {
        message.error(response.data.message || '发送安装指令失败');
      }
    } catch (error) {
      message.error('发送安装指令失败');
      console.error('Install error:', error);
    } finally {
      setInstalling(false);
    }
  };

  const columns = [
    {
      title: 'APK ID',
      dataIndex: 'apkId',
      key: 'apkId',
      ellipsis: true
    },
    {
      title: '文件名',
      dataIndex: 'fileName',
      key: 'fileName'
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version'
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size) => formatFileSize(size)
    },
    {
      title: 'MD5',
      dataIndex: 'md5',
      key: 'md5',
      ellipsis: true
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '上传时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'download',
                icon: <DownloadOutlined />,
                label: '下载',
                onClick: () => handleDownload(record.apkId)
              },
              {
                key: 'install',
                icon: <MobileOutlined />,
                label: '安装到设备',
                onClick: () => showInstallModal(record)
              },
              {
                type: 'divider'
              },
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: '删除',
                danger: true,
                onClick: () => {
                  Modal.confirm({
                    title: '确定要删除这个APK吗？',
                    content: '删除后将无法恢复',
                    okText: '确定',
                    cancelText: '取消',
                    okButtonProps: { danger: true },
                    onOk: () => handleDelete(record.apkId)
                  });
                }
              }
            ]
          }}
          trigger={['hover']}
        >
          <Button>
            操作 <DownOutlined />
          </Button>
        </Dropdown>
      )
    }
  ];

  return (
    <div>
      <Card
        title="APK管理"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUploadVisible(true)}
            >
              上传APK
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchAPKs}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={apks}
          columns={columns}
          rowKey="apkId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个APK`
          }}
        />
      </Card>

      <Modal
        title="上传APK"
        open={uploadVisible}
        onCancel={() => {
          setUploadVisible(false);
          form.resetFields();
          setUploadProgress(0);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpload}
        >
          <Form.Item
            name="file"
            label="APK文件"
            rules={[{ required: true, message: '请选择APK文件' }]}
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e && e.fileList;
            }}
          >
            <Upload
              accept=".apk"
              maxCount={1}
              beforeUpload={() => false}
              disabled={uploading}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="version"
            label="版本"
          >
            <Input placeholder="例如: 1.0.0" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="请输入APK描述" 
            />
          </Form.Item>

          {uploading && (
            <Form.Item>
              <Progress percent={uploadProgress} status="active" />
            </Form.Item>
          )}

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={uploading}
              >
                上传
              </Button>
              <Button 
                onClick={() => {
                  setUploadVisible(false);
                  form.resetFields();
                  setUploadProgress(0);
                }}
                disabled={uploading}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="安装到设备"
        open={installVisible}
        onCancel={() => setInstallVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setInstallVisible(false)}>
            取消
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={installing}
            onClick={() => installForm.submit()}
          >
            发送安装指令
          </Button>
        ]}
      >
        <Form
          form={installForm}
          layout="vertical"
          onFinish={handleInstall}
        >
          <Form.Item
            label="APK"
          >
            <Input value={selectedAPK?.fileName} disabled />
          </Form.Item>
          <Form.Item
            label="选择设备"
            name="deviceId"
            rules={[{ required: true, message: '请选择设备' }]}
          >
            <Select placeholder="请选择要安装的设备">
              {devices.map(device => (
                <Option key={device.deviceId} value={device.deviceId}>
                  {device.deviceName} ({device.deviceModel})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default APKList;
